import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { useUserSettingsStore } from '../../store/userSettingsStore';
import type { UserSettings as UserSettingsType } from '../../types/auth';

export const UserSettings: React.FC = () => {
  const { t } = useTranslation();
  const {
    userSettings,
    isLoading,
    error,
    setUserSettings,
    updateUserSettings,
    setSecurityMode,
    generateNewKeyPair,
    exportSettings,
    importSettings,
    resetSettings,
    setError,
  } = useUserSettingsStore();

  const [formData, setFormData] = React.useState<Partial<UserSettingsType>>({
    username: '',
    email: '',
    displayName: '',
  });  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [showKeyInfo, setShowKeyInfo] = React.useState(false);
  const [importData, setImportData] = React.useState('');
  const [showExport, setShowExport] = React.useState(false);
  const [showManualKeyInput, setShowManualKeyInput] = React.useState(false);
  const [manualPublicKey, setManualPublicKey] = React.useState('');
  const [manualPrivateKey, setManualPrivateKey] = React.useState('');

  React.useEffect(() => {
    if (userSettings) {
      setFormData({
        username: userSettings.username,
        email: userSettings.email,
        displayName: userSettings.displayName,
      });
    }
  }, [userSettings]);

  const handleInputChange = (field: keyof UserSettingsType, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!formData.username?.trim() || !formData.email?.trim()) {
      setError(t('userSettings.requiredFieldsError'));
      return;
    }

    const settings: UserSettingsType = {
      ...userSettings,
      username: formData.username.trim(),
      email: formData.email.trim(),
      displayName: formData.displayName?.trim() || '',
      securityMode: userSettings?.securityMode || false,
      publicKey: userSettings?.publicKey,
      privateKey: userSettings?.privateKey,
    };

    if (userSettings) {
      updateUserSettings(settings);
    } else {
      setUserSettings(settings);
    }
  };

  const handleSecurityModeToggle = async () => {
    const newSecurityMode = !userSettings?.securityMode;
    
    if (newSecurityMode) {
      const confirmed = window.confirm(
        t('userSettings.enableSecurityModeConfirm')
      );
      
      if (!confirmed) return;
    }

    const success = await setSecurityMode(newSecurityMode);
    if (success && newSecurityMode) {
      alert(t('userSettings.securityModeEnabled'));
    } else if (success && !newSecurityMode) {
      alert(t('userSettings.securityModeDisabled'));
    }
  };

  const handleGenerateNewKeys = async () => {
    const confirmed = window.confirm(
      t('userSettings.generateNewKeysConfirm')
    );
    
    if (!confirmed) return;

    const success = await generateNewKeyPair();
    if (success) {
      alert(t('userSettings.newKeysGenerated'));
    }
  };

  const handleExport = () => {
    const data = exportSettings();
    if (data) {
      setImportData(data);
      setShowExport(true);
    }
  };
  const handleImport = () => {
    if (!importData.trim()) {
      setError(t('common.required'));
      return;
    }

    const success = importSettings(importData.trim());
    if (success) {
      setImportData('');
      alert(t('common.success'));
    }
  };

  const handleManualKeyInput = async () => {
    if (!manualPublicKey.trim() || !manualPrivateKey.trim()) {
      setError(t('userSettings.bothKeysRequired'));
      return;
    }

    try {
      // 鍵の形式を簡単に検証
      if (!manualPublicKey.includes('-----BEGIN') && !manualPublicKey.match(/^[A-Za-z0-9+/]+=*$/)) {
        setError(t('userSettings.invalidPublicKeyFormat'));
        return;
      }
      
      if (!manualPrivateKey.includes('-----BEGIN') && !manualPrivateKey.match(/^[A-Za-z0-9+/]+=*$/)) {
        setError(t('userSettings.invalidPrivateKeyFormat'));
        return;
      }

      const settings: UserSettingsType = {
        ...userSettings,
        username: userSettings?.username || formData.username || '',
        email: userSettings?.email || formData.email || '',
        displayName: userSettings?.displayName || formData.displayName || '',
        securityMode: true,
        publicKey: manualPublicKey.trim(),
        privateKey: manualPrivateKey.trim(),
      };

      if (userSettings) {
        updateUserSettings(settings);
      } else {
        setUserSettings(settings);
      }

      setShowManualKeyInput(false);
      setManualPublicKey('');
      setManualPrivateKey('');
      alert(t('userSettings.keysSet'));
    } catch (error) {
      console.error('Manual key input error:', error);
      setError(t('userSettings.keySetError'));
    }
  };

  const handleCancelManualKeyInput = () => {
    setShowManualKeyInput(false);
    setManualPublicKey('');
    setManualPrivateKey('');
  };

  const handleReset = () => {
    const confirmed = window.confirm(
      t('confirm.delete')
    );
    
    if (confirmed) {
      resetSettings();
      setFormData({
        username: '',
        email: '',
        displayName: '',
      });
      alert(t('common.success'));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(t('userSettings.copiedToClipboard'));
    }).catch(() => {
      alert(t('userSettings.copyFailed'));
    });
  };

  return (
    <div className="user-settings">
      <div className="user-settings__header">
        <h2 className="user-settings__title">{t('userSettings.title')}</h2>
        <p className="user-settings__description">
          {t('userSettings.description')}
        </p>
      </div>

      {error && (
        <div className="alert alert--error">
          <div className="alert__icon">!</div>
          <div className="alert__content">
            <p className="alert__text">{error}</p>
          </div>
          <button
            className="alert__close"
            onClick={() => setError(null)}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>
      )}      <div className="user-settings__content">
        <div className="settings-grid">
          {/* 基本情報 */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t('userSettings.basicInfo')}</h3>
            
            <div className="settings-form">
              <div className="input-wrapper input-wrapper--required">
                <Input
                  label={t('userSettings.username')}
                  placeholder={t('userSettings.usernamePlaceholder')}
                  value={formData.username || ''}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  required
                />
              </div>
              
              <div className="input-wrapper input-wrapper--required">
                <Input
                  label={t('userSettings.email')}
                  type="email"
                  placeholder={t('userSettings.emailPlaceholder')}
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              
              <Input
                label={t('userSettings.displayName')}
                placeholder={t('userSettings.displayNamePlaceholder')}
                value={formData.displayName || ''}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                helpText={t('userSettings.displayNameHelp')}
              />
            </div>

            <div className="settings-actions">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? t('userSettings.saving') : t('userSettings.saveSettings')}
              </Button>
            </div>
          </section>

          {/* セキュリティ設定 */}
          <section className="settings-section">
          <h3 className="settings-section__title">{t('userSettings.securitySettings')}</h3>
          
          <div className="security-setting">
            <div className="security-setting__info">
              <h4 className="security-setting__name">{t('userSettings.securityMode')}</h4>
              <p className="security-setting__description">
                {t('userSettings.securityModeDescription')}
              </p>
            </div>
            
            <div className="security-setting__control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={userSettings?.securityMode || false}
                  onChange={handleSecurityModeToggle}
                  disabled={isLoading}
                />
                <span className="toggle-switch__slider"></span>
              </label>
              <span className={`security-setting__status ${userSettings?.securityMode ? 'security-setting__status--on' : 'security-setting__status--off'}`}>
                {userSettings?.securityMode ? t('userSettings.securityOn') : t('userSettings.securityOff')}
              </span>
            </div>
          </div>

          {userSettings?.securityMode && (
            <div className="security-details">
              <div className="key-info">
                <div className="key-info__header">
                  <h4 className="key-info__title">{t('userSettings.securityCode')}</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowKeyInfo(!showKeyInfo)}
                  >
                    {showKeyInfo ? t('userSettings.hide') : t('userSettings.show')}
                  </Button>
                </div>
                
                {showKeyInfo && (
                  <div className="key-info__content">
                    <div className="key-field">
                      <label className="key-field__label">{t('userSettings.encryptionCode')}</label>
                      <div className="key-field__value">
                        <code className="key-field__code">
                          {userSettings.publicKey?.substring(0, 50)}...
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => userSettings.publicKey && copyToClipboard(userSettings.publicKey)}
                        >
                          {t('userSettings.copy')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="key-field">
                      <label className="key-field__label">{t('userSettings.recoveryCode')}</label>
                      <div className="key-field__value">
                        <code className="key-field__code">
                          {userSettings.privateKey?.substring(0, 50)}...
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => userSettings.privateKey && copyToClipboard(userSettings.privateKey)}
                        >
                          {t('userSettings.copy')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>              <div className="key-actions">
                <Button
                  variant="secondary"
                  onClick={handleGenerateNewKeys}
                  disabled={isLoading}
                >
                  {t('userSettings.generateNewKeys')}
                </Button>                <Button
                  variant="secondary"
                  onClick={() => setShowManualKeyInput(true)}
                  disabled={isLoading}
                >
                  {t('userSettings.manualKeyInput')}
                </Button>
              </div>
            </div>
          )}

          {/* 共有データ閲覧用の手動鍵入力 */}
          {!userSettings?.securityMode && (
            <div className="manual-key-section">
              <div className="manual-key-info">
                <h4 className="manual-key-info__title">{t('userSettings.viewSharedData')}</h4>
                <p className="manual-key-info__description">
                  {t('userSettings.viewSharedDataDescription')}
                </p>
              </div>              <Button
                variant="secondary"
                onClick={() => setShowManualKeyInput(true)}
                disabled={isLoading}
              >
                {t('userSettings.inputEncryptionKey')}
              </Button>
            </div>
          )}
        </section>

        {/* 高度な設定 */}
        {/* <section className="settings-section">
          <h3 className="settings-section__title">
            <button
              className="settings-section__toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              ⚙️ 高度な設定 {showAdvanced ? '▼' : '▶'}
            </button>
          </h3>
          
          {showAdvanced && (
            <div className="advanced-settings">
              <div className="import-export">
                <h4 className="import-export__title">📦 設定のバックアップ・復元</h4>
                <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  設定をエクスポートしてバックアップを作成したり、他のデバイスに設定をインポートできます。
                </p>
                
                <div className="import-export__actions">
                  <Button
                    variant="secondary"
                    onClick={handleExport}
                  >
                    📤 設定をエクスポート
                  </Button>
                </div>

                <div className="import-section">
                  <label htmlFor="import-data" className="import-section__label">
                    📥 設定をインポート
                  </label>
                  <textarea
                    id="import-data"
                    className="import-section__textarea"
                    placeholder="エクスポートした設定データ（JSON形式）を貼り付けてください"
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    rows={6}
                  />
                  <Button
                    variant="secondary"
                    onClick={handleImport}
                    disabled={!importData.trim()}
                  >
                    📥 インポート実行
                  </Button>
                </div>

                {showExport && (
                  <div className="export-result">
                    <label className="export-result__label">
                      📋 エクスポートデータ（コピーして安全な場所に保存してください）
                    </label>
                    <textarea
                      className="export-result__textarea"
                      value={importData}
                      readOnly
                      rows={6}
                    />
                    <Button
                      variant="ghost"
                      onClick={() => copyToClipboard(importData)}
                    >
                      📋 クリップボードにコピー
                    </Button>
                  </div>
                )}
              </div>

              <div className="danger-zone">
                <h4 className="danger-zone__title">🚨 危険な操作</h4>
                <p className="danger-zone__description">
                  以下の操作は元に戻すことができません。実行前に設定のエクスポートを行うことを強く推奨します。
                </p>
                <Button
                  variant="error"
                  onClick={handleReset}
                >
                  🗑️ すべての設定をリセット
                </Button>
              </div>
            </div>
          )}        </section> */}
        </div>
      </div>

      {/* 手動鍵入力モーダル */}
      {showManualKeyInput && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">{t('userSettings.manualKeyTitle')}</h3>
              <button
                className="modal-close"
                onClick={handleCancelManualKeyInput}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                {t('userSettings.manualKeyDescription')}
              </p>
              
              <div className="manual-key-inputs">
                <div className="input-wrapper">
                  <label className="input-label">{t('userSettings.publicKey')}</label>
                  <textarea
                    className="input textarea"
                    placeholder={t('userSettings.publicKeyPlaceholder')}
                    value={manualPublicKey}
                    onChange={(e) => setManualPublicKey(e.target.value)}
                    rows={6}
                  />
                </div>
                
                <div className="input-wrapper">
                  <label className="input-label">{t('userSettings.privateKey')}</label>
                  <textarea
                    className="input textarea"
                    placeholder={t('userSettings.privateKeyPlaceholder')}
                    value={manualPrivateKey}
                    onChange={(e) => setManualPrivateKey(e.target.value)}
                    rows={6}
                  />
                </div>
              </div>
              
              <div className="modal-warning">
                <div className="warning-icon">⚠️</div>
                <div className="warning-text">
                  <strong>{t('userSettings.keyWarning')}</strong>{t('userSettings.keyWarningText')}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <Button
                variant="ghost"
                onClick={handleCancelManualKeyInput}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleManualKeyInput}
                disabled={!manualPublicKey.trim() || !manualPrivateKey.trim()}
              >
                {t('userSettings.setKeys')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};